<template>
  <div class="grid-x grid-padding-x autocomplete">
    <div :class="containerClassName">
      <label :for="id">{{label}}<span v-if="required"> *</span></label>

      <div class="input-container no-margin" :class="inputContainerClasses">
        <div class="vue-treeselect custom vue-treeselect--single vue-treeselect--searchable vue-treeselect--open-below" :class="[mainTreeSelectClass]">
          <div class="vue-treeselect__control">
            <div class="vue-treeselect__value-container">
              <div class="vue-treeselect__single-value">
                <div></div>
              </div>

              <div class="vue-treeselect__placeholder vue-treeselect-helper-zoom-effect-off" :class="{'vue-treeselect-helper-hide': (value !== '' && value !== null)}">{{placeholder}}</div>
              <div class="vue-treeselect__input-container">
                <input
                  type="text"
                  class="vue-treeselect__input"
                  :id="id"
                  :name="name"
                  :value="value"
                  :class="className"
                  :disabled="isDisabled"
                  autocomplete="off"
                  tabindex="0"
                  @input="onInput"
                  @focus="onFocus"
                  @keydown="onKeydown"
                >
              </div>
            </div>

            <div class="vue-treeselect__x-container" title="Clear value" @click="onClearInput()"></div>
            <div class="vue-treeselect__control-arrow-container"></div>
          </div>

          <div class="vue-treeselect__menu-container" style="z-index: 999;">
            <div class="vue-treeselect__menu vue-treeselect__menu--transition-leave vue-treeselect__menu--transition-leave-active">
              <div class="vue-treeselect__list">
                <div v-if="items.length < 1 || searching" class="vue-treeselect__list-item vue-treeselect__indent-level-0">
                  <div class="vue-treeselect__label-container">
                    <label class="vue-treeselect__label">{{ noItemsText || 'Laster...'}}</label>
                  </div>
                </div>

                <template v-if="items.length > 0">
                  <div v-for="(item, index) in items" v-bind:key="index" class="vue-treeselect__list-item vue-treeselect__indent-level-0">
                    <div class="vue-treeselect__option" :data-item-index="index" @click.prevent="onSelectItem(index, item)">
                      <div class="vue-treeselect__label-container">
                        <label class="vue-treeselect__label">
                          <slot name="item" :item="item">
                              {{item}}
                          </slot>
                        </label>
                      </div>
                    </div>
                  </div>
                </template>
              </div>

              <div class="vue-treeselect__menu-footer" v-if="canCreate || canUpdate">
                <a href="javascript:;" v-if="canCreate" @click="onCreate()" class="button action glyph"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-add"></use></svg> <span>Opprett</span></a>
                <a href="javascript:;" v-if="canUpdate" @click="onUpdate()" class="button action glyph glyph-only"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-edit"></use></svg> <span>Rediger</span></a>
              </div>
            </div>
          </div>
        </div>

        <div class="input-quick-suggestions" v-if="quicklinks && quicklinks.length > 0">
          <svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-history"></use></svg>
          <ul>
            <template v-for="link in quicklinks">
              <slot name="quicklink" :quicklink="link">{{link}}</slot>
            </template>
          </ul>
        </div>
        <span class="form-error-label">
          <svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-error"></use></svg>
          {{ customErrorMessage ? customErrorMessage : errors.first(name) }}
        </span>
      </div>
    </div>
    <div class="cell shrink">
      <div class="input-container no-margin">
        <label>&nbsp;</label>
        <a v-if="isDisabled && !searching" class="button submit primary" disabled>Søk</a>
        <a v-else-if="!searching" class="button submit primary" @click.prevent="onSearchPressed($event)">Søk</a>
        <a v-else class="button submit primary loading">Søker...<span class="loader-spinner"></span></a>
      </div>
    </div>
  </div>
</template>

<script>
import DummyCache from '@/plugins/dummy-cache.js'

export default {
  name: 'TheAutoCompleteInputButton',
  inject: [
    '$validator'
  ],
  inheritAttributes: true,
  props: {
    id: {
      type: String,
      default: function () {
        return 'autocomplete-' + this._uid
      }
    },
    containerClassName: {
      type: String,
      default: 'cell auto'
    },
    customErrorMessage: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: null
    },
    value: {
      default: ''
    },
    placeholder: {
      type: String,
      required: false,
      default: 'Søk'
    },
    disabled: {
      type: [Boolean, Number],
      required: false,
      default: false
    },
    label: {
      type: String,
      required: false,
      default: 'Søk'
    },
    className: {
      type: String,
      required: false,
      default: 'searchinput'
    },
    required: {
      type: Boolean,
      default: false
    },
    onInputCallback: {
      type: Function,
      required: true
    },
    onSelectCallback: {
      type: Function,
      required: true
    },
    onCreateCallback: {
      type: Function,
      required: false
    },
    onUpdateCallback: {
      type: Function,
      required: false
    },
    onResetCallback: {
      type: Function,
      required: false
    },
    quicklinks: {
      type: Array,
      required: false,
      default: null
    }
  },
  data () {
    return {
      uuid: 0,
      items: [],
      cache: null,
      searching: false,
      nothingfound: false,
      hasFocus: false
    }
  },
  created () {
    // Create a cache container
    this.cache = new DummyCache()
  },
  beforeMount () {
    // On click outside
    var vm = this
    window.jQuery(document).mouseup(function (e) {
      var container = window.jQuery('.autocomplete')
      // if the target of the click isn't the container nor a descendant of the container
      if (!container.is(e.target) && container.has(e.target).length === 0) {
        vm.hasFocus = false
      }
    })
  },
  watch: {
    items: function () {
      if (this.items.length > 0) {
        this.hasFocus = true
      } else if (this.nothingfound) {
        this.hasFocus = true
      } else {
        this.hasFocus = false
      }
    }
  },
  computed: {
    canCreate: function () {
      return this.onCreateCallback !== undefined
    },
    canUpdate: function () {
      return this.onUpdateCallback !== undefined && !this._.isEmpty(this.value)
    },
    isDisabled: function () {
      return this.disabled || this.searching
    },
    noItemsText: function () {
      if (this.searching) {
        return 'Søker...'
      }

      if (this.nothingfound) {
        return 'Ingen resultater funnet...'
      }

      if (this.items.length > 0) {
        return ''
      }

      return 'Skriv noe for å søke...'
    },
    mainTreeSelectClass: function () {
      let cls = []

      if (this.hasFocus) {
        cls.push('vue-treeselect--open')
        cls.push('vue-treeselect--search-active')
      }

      if (this.value !== '' && this.value !== null) {
        cls.push('vue-treeselect--has-value')
      }

      if (!(this.canCreate || this.canUpdate)) {
        cls.push('no-footer')
      }

      return cls.length > 0 ? cls.join(' ') : ''
    },
    inputContainerClasses: function () {
      let cls = []

      if (this.errors.has(this.name)) {
        cls.push('form-error')
      }

      if (this.quicklinks && this.quicklinks.length > 0) {
        cls.push('quick-suggestions')
      }

      return cls
    }
  },
  methods: {
    skip: function () {
      this.items = []

      this.onSelectCallback(null)
    },
    onCreate: function (e) {
      this.onCreateCallback(null)
    },
    onUpdate: function (e) {
      this.onUpdateCallback(this.value)
    },
    onFocus: function (e) {
      if (this.value !== null && this.value.length > 0) {
        this.hasFocus = true
      }
    },
    onKeydown: function (e) {
      if (e.keyCode === 13) {
        e.preventDefault()
        this.onSearchPressed()
      }
    },
    onInput: function (e) {
      this.$emit('input', e.target.value)
    },
    onClearInput: function () {
      this.$emit('input', '')

      if (this.onResetCallback !== undefined) {
        this.onResetCallback()
      }

      this.hasFocus = false
      this.items = []
    },
    onSelectItem: function (index, item) {
      window.jQuery('.vue-treeselect__option').removeClass('vue-treeselect__option--selected')
      window.jQuery('[data-item-index="' + index + '"]').addClass('vue-treeselect__option--selected')

      // Run select callback
      this.onSelectCallback(item)

      this.hasFocus = false
    },
    onSearchPressed: function (e) {
      let query = this.value || ''

      // Create a promise variable
      let promise = null

      // Trim value
      let trimmed = this._.toLower(query.trim())

      // No value - reset
      if (trimmed.length < 1) {
        // No query, so reset selected
        this.onClearInput()
        return
      }

      this.hasFocus = false
      this.nothingfound = false
      this.searching = true
      this.items = []

      // Try to load promise from cache - else run find
      promise = this.onInputCallback(trimmed)

      // Do things with promise
      promise
        .then((data) => {
          this.items = data.data || []
        })
        .catch((error) => {
          console.log('Failed Loading')
          console.log(error)
        })
        .then(() => {
          this.searching = false
          this.nothingfound = this.items.length < 1

          if (this.nothingfound) {
            this.hasFocus = true
          }

          let element = window.jQuery('input#' + this.id)

          // Must have timeout around this for it to work.
          setTimeout(function () {
            // Put element in focus again...
            element.focus()
          })
        })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Inputs/TheAutoCompleteInputButton.vue